import { useAxios } from "@hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface ProctoringTerm {
  examTermId: string;
  examTitle: string;
  date: string;
  state: number;
  candidateCount: number;
  assignedToMeCount: number;
}

export interface ProctoringCandidate {
  examInstanceId: string;
  identity: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  examInstanceState: number;
  assessorId?: string | null;
  assessorName?: string | null;
}

export interface ProctoringSession {
  examTermId: string;
  examTitle: string;
  serverUrl: string;
  roomName: string;
  token: string;
  canManageAssignments: boolean;
  currentUserId: string;
  isAssessor: boolean;
  candidates: ProctoringCandidate[];
}

export interface ProctoringAssessor {
  assessorId: string;
  name: string;
  email?: string;
}

export interface ProctoringAssignments {
  assessors: ProctoringAssessor[];
  candidates: ProctoringCandidate[];
}

export interface ProctoringAssignmentItem {
  examInstanceId: string;
  assessorId: string | null;
}

export interface CandidateMediaCommand {
  examInstanceId: string;
  camera?: boolean;
  microphone?: boolean;
}

/// Asks a candidate's player to switch its camera/microphone; the result shows up as track mute events.
export const useSetCandidateMedia = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ examInstanceId, camera, microphone }: CandidateMediaCommand) => {
      await axios.post(`/api/proctoring/${examTermId}/candidates/${examInstanceId}/media`, { camera, microphone });
    },
  });
};

/// Open conversation through the LiveKit room metadata. Scope "mine" opens it only among the caller's own
/// candidates (always for assessors); "all" (admins) opens it for the whole room.
export const useSetOpenAudio = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ enabled, scope }: { enabled: boolean; scope: "mine" | "all" }) => {
      await axios.post(`/api/proctoring/${examTermId}/open-audio`, { enabled, scope });
    },
  });
};

/// Lets waiting candidates into the exam; without ids every waiting candidate the user may see.
export const useAdmitCandidates = (examTermId?: string) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (examInstanceIds?: string[]) =>
      (await axios.post<{ admitted: number }>(`/api/proctoring/${examTermId}/admit`, { examInstanceIds })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proctoringSession", examTermId] });
      queryClient.invalidateQueries({ queryKey: ["proctoringProgress", examTermId] });
    },
  });
};

export type AudioMode = "auto" | "open" | "listen" | "muted";

/// Who hears whom: for the caller's own candidates, or (admins, scope "all") for the whole room.
export const useSetAudioMode = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ mode, scope }: { mode: AudioMode; scope: "mine" | "all" }) => {
      await axios.post(`/api/proctoring/${examTermId}/audio-mode`, { mode, scope });
    },
  });
};

export interface ProctoringTimerSync {
  examInstanceId: string;
  serverNowUnixMs: number;
  remainingSeconds: number | null;
  endsAtUnixMs: number | null;
  isRunning: boolean;
  state: number;
}

/// Same figures as the term monitoring page, for the candidates the user may watch.
export interface ProctoringProgress {
  examInstanceId: string;
  state: number;
  sync?: ProctoringTimerSync | null;
  plannedSeconds?: number | null;
  answered: number;
  total: number;
  percentage: number;
  startedAt?: string | null;
  browserExits: number;
  tabSwitches: number;
}

export const useGetProctoringProgress = (examTermId?: string) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringProgress", examTermId],
    queryFn: async () => (await axios.get<ProctoringProgress[]>(`/api/proctoring/${examTermId}/progress`)).data,
    enabled: !!examTermId,
    refetchInterval: 10000,
    retry: 1,
  });
};

export type CandidateStateAction = "pause" | "resume" | "exclude";

/// Pause, resume or exclude one candidate, as on the monitoring page (assessors: own candidates only).
export const useChangeCandidateState = (examTermId?: string) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ examInstanceId, action }: { examInstanceId: string; action: CandidateStateAction }) =>
      (await axios.post<{ changed: boolean; state: number }>(`/api/proctoring/${examTermId}/candidates/${examInstanceId}/state`, { action })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proctoringProgress", examTermId] });
      queryClient.invalidateQueries({ queryKey: ["proctoringSession", examTermId] });
    },
  });
};

/// E-mails assessors a personal sign-in link to the proctoring wall.
export const useInviteAssessors = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ assessorIds, note }: { assessorIds?: string[]; note?: string }) =>
      (await axios.post<{ sent: string[]; failed: string[] }>(`/api/proctoring/${examTermId}/invite-assessors`, { assessorIds, note })).data,
  });
};

export const useGetProctoringTerms = () => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringTerms"],
    queryFn: async () => (await axios.get<ProctoringTerm[]>("/api/proctoring/terms")).data,
    refetchInterval: 30000,
  });
};

/// Candidates/assignments refresh periodically; the LiveKit token is only used on first connect.
export const useGetProctoringSession = (examTermId?: string) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringSession", examTermId],
    queryFn: async () => (await axios.get<ProctoringSession>(`/api/proctoring/${examTermId}/session`)).data,
    enabled: !!examTermId,
    refetchInterval: 30000,
    retry: 1,
  });
};

export const useGetProctoringAssignments = (examTermId: string, enabled: boolean) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringAssignments", examTermId],
    queryFn: async () => (await axios.get<ProctoringAssignments>(`/api/proctoring/${examTermId}/assignments`)).data,
    enabled,
  });
};

export const useSaveProctoringAssignments = (examTermId: string) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: ProctoringAssignmentItem[]) => {
      await axios.put(`/api/proctoring/${examTermId}/assignments`, items);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proctoringAssignments", examTermId] });
      queryClient.invalidateQueries({ queryKey: ["proctoringSession", examTermId] });
    },
  });
};
